import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { BootstrapDialog } from 'components/BootstrapDialog'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { FileUpload } from 'components/FileUpload'
import { RequiredStar } from 'components/RequiredStarSpan'
import dayjs, { Dayjs } from 'dayjs'
import { FunctionComponent, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IFile } from 'types/file'

enum ProcurementPlanReason {
  FIRST_TIME_CREATION = 'FIRST_TIME_CREATION',
  BUDGET_CHANGE = 'BUDGET_CHANGE',
  URGENT_NEED = 'URGENT_NEED',
}

export interface PublishFormData {
  changeReason: string
  reasonDescription: string
  creationOrderDate?: string | Dayjs
  file: IFile | null
}

interface Props {
  handleClose: () => void
  onCreate: (data: PublishFormData) => void
  defaultValues?: Partial<PublishFormData>
}

export const PublishForm: FunctionComponent<Props> = ({
  handleClose,
  onCreate,
  defaultValues,
}) => {
  const { t } = useTranslation()
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<PublishFormData>({
    defaultValues: {
      changeReason: defaultValues?.changeReason ?? '',
      reasonDescription: defaultValues?.reasonDescription ?? '',
      creationOrderDate: defaultValues?.creationOrderDate
        ? dayjs(new Date(defaultValues.creationOrderDate as string))
        : '',
      file: defaultValues?.file ?? null,
    },
  })

  useEffect(() => {
    if (defaultValues?.file) {
      setValue('file', defaultValues.file)
    }
  }, [defaultValues?.file, setValue])

  const onSubmit = (data: PublishFormData) => {
    onCreate(data)
  }

  return (
    <BootstrapDialog open onClose={handleClose}>
      <DialogTitle sx={{ m: 0, p: 2 }}>{t('procurement-plan')}</DialogTitle>

      <DialogCloseButton handleClose={handleClose} />

      <DialogContent dividers>
        <form onSubmit={handleSubmit(onSubmit)} id="procurement-publish-form">
          <FormControl sx={{ minWidth: 240 }} fullWidth margin="normal">
            <InputLabel id="reason-select-helper-label">
              {t('changeReason')} <RequiredStar>*</RequiredStar>
            </InputLabel>

            <Controller
              name="changeReason"
              control={control}
              rules={{ required: t('required') }}
              render={({ field }) => (
                <Select
                  {...field}
                  error={!!errors.changeReason}
                  labelId="reason-select-helper-label"
                  label={
                    <>
                      {t('changeReason')} <RequiredStar>*</RequiredStar>
                    </>
                  }
                >
                  {Object.keys(ProcurementPlanReason).map((reason) => (
                    <MenuItem value={reason} key={reason}>
                      {t(reason)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />

            {errors.changeReason && (
              <FormHelperText error>{errors.changeReason?.message}</FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 240 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('reasonDescription')} <RequiredStar>*</RequiredStar>
                </>
              }
              multiline
              minRows={4}
              variant="outlined"
              sx={{ resize: 'both' }}
              error={!!errors.reasonDescription}
              {...register('reasonDescription', { required: t('required') })}
            />
            {errors.reasonDescription && (
              <FormHelperText error>
                {errors.reasonDescription?.message}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 240 }} fullWidth margin="normal">
            <Controller
              name="creationOrderDate"
              control={control}
              rules={{ required: t('required') }}
              render={({
                field: { ref, onBlur, name, onChange, ...field },
                fieldState,
              }) => (
                <DatePicker
                  {...field}
                  inputRef={ref}
                  label={
                    <>
                      {t('creationOrderDate')} <RequiredStar>*</RequiredStar>
                    </>
                  }
                  onChange={(val) => {
                    onChange(dayjs(val).toISOString())
                  }}
                  slotProps={{
                    textField: {
                      onBlur,
                      name,
                      error: !!fieldState?.error,
                      helperText: fieldState?.error?.message,
                    },
                  }}
                />
              )}
            />
          </FormControl>

          <input type="hidden" {...register('file', { required: t('required') })} />
          <FileUpload
            text={t('upload-procurement-excel')}
            handleFile={(file) => {
              setValue('file', file)
            }}
            isRequired
            file={defaultValues?.file ?? undefined}
          />
          {errors.file && (
            <FormHelperText error>{String(errors.file?.message)}</FormHelperText>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          type="submit"
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          form="procurement-publish-form"
        >
          {t('publish')}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  )
}
